import { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { RagImageItem } from "../../types/ragChatTypes";
import { RagImageLightbox } from "./RagImageLightbox";

const SECTION_COUNT_PILL_SX = {
  ml: 1,
  px: 1,
  py: 0.25,
  borderRadius: 10,
  bgcolor: "grey.200",
  color: "text.secondary",
  fontSize: 12,
  fontWeight: 700,
  minWidth: 22,
  textAlign: "center" as const,
};

const MEDIA_CARD_SX = {
  borderRadius: 1.5,
  overflow: "hidden",
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
  boxShadow: "0 1px 4px rgba(28,46,54,0.08)",
  transition: "transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(28,46,54,0.14)",
  },
  "&:focus-visible": {
    outline: "2px solid",
    outlineColor: "primary.main",
    outlineOffset: 2,
  },
};

interface RagMoreDiagramsCarouselProps {
  images: RagImageItem[];
  msgId: string;
  /** Prefix for page labels in lightbox, e.g. "p." */
  pageLabel?: string;
  /** Show section header with count pill. */
  showHeader?: boolean;
  /** Top border separator (procedure rail style). */
  bordered?: boolean;
}

/**
 * Compact horizontal carousel for secondary diagrams — same card/scroll
 * pattern as {@link RagImageRail} / General Videos media cards.
 */
export function RagMoreDiagramsCarousel({
  images,
  msgId,
  pageLabel = "p.",
  showHeader = true,
  bordered = false,
}: RagMoreDiagramsCarouselProps) {
  const { t } = useTranslation();
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  const items = useMemo(() => images.filter((img) => Boolean(img.path)), [images]);
  if (!items.length) return null;

  const gallery = items.map((img) => ({
    src: img.path,
    label: `${pageLabel}${String(img.metadata?.page ?? "?")}`,
  }));

  return (
    <Box
      sx={{
        mt: bordered ? 2 : 0,
        pt: bordered ? 2 : 0,
        ...(bordered
          ? { borderTop: "1px solid", borderColor: "divider" }
          : {}),
      }}
    >
      {showHeader && (
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Typography variant="labelSmall" color="text.secondary" fontWeight={700}>
            {t("opva.chatAssistanceMoreDiagrams")}
          </Typography>
          <Box component="span" sx={SECTION_COUNT_PILL_SX}>
            {items.length}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          gap: 1.25,
          overflowX: "auto",
          pb: 0.5,
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
          "&::-webkit-scrollbar": { height: 6 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "grey.300",
            borderRadius: 3,
          },
        }}
      >
        {items.map((item, idx) => {
          const page = String(item.metadata?.page ?? "?");
          const file = (item.path || "fig").split("/").pop() || "fig";
          return (
            <Box
              component="button"
              type="button"
              key={`${msgId}-more-diag-${page}-${idx}`}
              onClick={() => setLightboxSrc(item.path)}
              aria-label={`${t("opva.chatAssistanceClickToZoom")} ${pageLabel}${page}`}
              title={file}
              sx={{
                ...MEDIA_CARD_SX,
                flex: "0 0 auto",
                width: { xs: 200, sm: 240 },
                p: 0,
                textAlign: "left",
                cursor: "zoom-in",
                scrollSnapAlign: "start",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  aspectRatio: "4 / 3",
                  bgcolor: "#eef3f8",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src={item.path}
                  alt={`${pageLabel}${page} diagram`}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              </Box>
              <Typography
                variant="labelSmall"
                sx={{
                  px: 1.25,
                  py: 0.6,
                  bgcolor: "grey.50",
                  color: "text.secondary",
                  fontWeight: 600,
                }}
              >
                {pageLabel}
                {page}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <RagImageLightbox
        src={lightboxSrc}
        gallery={gallery}
        onClose={() => setLightboxSrc(null)}
      />
    </Box>
  );
}
